import { useRef, useCallback, useEffect, useState } from 'react';

/**
 * Custom hook for managing chat container heights with viewport-based calculations.
 * Handles dynamic input resizing and provides refs for container elements.
 * 
 * The hook supports two patterns:
 * 1. Simple pattern: Only scrollRef (height applied directly to scroll container)
 * 2. Wrapper pattern: Both bodyRef and scrollRef (height applied to wrapper, scroll fills it)
 * 
 * @param {Object} config - Configuration object
 * @param {number} config.headerHeight - Fixed header height in pixels
 * @param {number} config.footerBaseHeight - Base footer height (excluding dynamic input)
 * @param {number} [config.statusBarHeight=0] - Optional status bar height
 * @param {boolean} [config.showStatusBar=false] - Whether status bar is visible
 * @param {number} [config.paddingBuffer=0] - Additional padding/spacing buffer
 * @param {number} [config.minInputHeight=48] - Minimum input field height
 * @param {boolean} [config.enabled=true] - Enable/disable height calculations
 * 
 * @returns {Object} Hook result object
 * @returns {React.RefObject} inputRef - Ref for the input element
 * @returns {React.RefObject} scrollRef - Ref for the scrollable messages container
 * @returns {React.RefObject} bodyRef - Ref for the body wrapper element (optional)
 * @returns {Function} recalculate - Manual recalculation trigger function
 * 
 * @example
 * // Simple pattern (ChatThread) - height applied to scrollRef directly
 * const { inputRef, scrollRef, recalculate } = useChatContainerHeight({
 *   headerHeight: 64,
 *   footerBaseHeight: 100,
 *   paddingBuffer: 25,
 * });
 * 
 * @example
 * // Wrapper pattern (SupportMessageList) - height applied to bodyRef, scroll fills it
 * const { inputRef, scrollRef, bodyRef, recalculate } = useChatContainerHeight({
 *   headerHeight: 64,
 *   footerBaseHeight: 90,
 *   statusBarHeight: 35,
 *   showStatusBar: isStatusVisible,
 * });
 * 
 * @example
 * // AI chat with custom configuration
 * const { inputRef, scrollRef, bodyRef } = useChatContainerHeight({
 *   headerHeight: 80,
 *   footerBaseHeight: 100,
 *   paddingBuffer: 20,
 *   minInputHeight: 56,
 * });
 */
const useChatContainerHeight = ({
  headerHeight,
  footerBaseHeight,
  statusBarHeight = 0,
  showStatusBar = false,
  paddingBuffer = 0,
  minInputHeight = 48,
  enabled = true,
  inputCounterVisibleHeight,
  inputCounterHiddenHeight
} = {}) => {
  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const bodyRef = useRef(null);
  const [showInputCharacterCounter, setShowInputCharacterCounter] = useState(false);

  /**
   * Calculate and apply container heights based on viewport and UI elements.
   * Uses CSS calc() for responsive viewport-based sizing.
   */
  const calculateHeight = useCallback(() => {
    if (!enabled || !inputRef.current || !scrollRef.current) {
      return;
    }

    // Get actual input height or fallback to minimum
    const inputHeight = inputRef.current.offsetHeight || minInputHeight;

    // check input height for multiline
    let shouldShowCounter = false;
    if (inputCounterVisibleHeight && inputCounterHiddenHeight) {
      shouldShowCounter = Math.round(inputHeight / minInputHeight) > 1;
      setShowInputCharacterCounter(shouldShowCounter);
    }
    
    // Calculate total non-content height
    const activeStatusHeight = showStatusBar ? statusBarHeight : 0;
    const totalNonContentHeight = 
      headerHeight + 
      footerBaseHeight + 
      inputHeight + 
      activeStatusHeight + 
      paddingBuffer + 
      (shouldShowCounter ? inputCounterVisibleHeight : inputCounterHiddenHeight);
    
    // Apply viewport-based height calculation
    const calculatedHeight = `calc(100vh - ${totalNonContentHeight}px)`;
    
    // Set body wrapper height (if using bodyRef pattern)
    if (bodyRef.current) {
      bodyRef.current.style.height = calculatedHeight;
      bodyRef.current.style.maxHeight = calculatedHeight;
    }
    
    // Set scroll container height (always required)
    scrollRef.current.style.height = calculatedHeight;
    scrollRef.current.style.maxHeight = calculatedHeight;
    scrollRef.current.style.flex = '1';
    scrollRef.current.style.minHeight = '0';
  }, [
    enabled,
    headerHeight,
    footerBaseHeight,
    statusBarHeight,
    showStatusBar,
    paddingBuffer,
    minInputHeight,
    inputCounterVisibleHeight,
    inputCounterHiddenHeight,
  ]);

  /**
   * Set up ResizeObserver to watch for input height changes.
   * Automatically recalculates container heights when input resizes (e.g., multiline expansion).
   */
  useEffect(() => {
    if (!enabled || !inputRef.current) {
      return;
    }

    const observer = new ResizeObserver(() => {
      calculateHeight();
    });

    observer.observe(inputRef.current);

    // Initial calculation with small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => calculateHeight(), 100);

    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
    };
  }, [calculateHeight, enabled]);

  /**
   * Recalculate when configuration dependencies change.
   * Useful when status bars toggle visibility or other UI elements change.
   */
  useEffect(() => {
    if (enabled) {
      const timeoutId = setTimeout(() => calculateHeight(), 100);
      return () => clearTimeout(timeoutId);
    }
  }, [calculateHeight, enabled, showStatusBar]);

  return {
    inputRef,
    scrollRef,
    bodyRef,
    recalculate: calculateHeight,
    showInputCharacterCounter
  };
};

export default useChatContainerHeight;
